import React, { useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Link,
  IconButton,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { confirmForgotPassword } from "../services/authService";
import { colors, typography } from '../../styles/theme';
import { getResetPasswordParams } from '../utils/authQueryParams';
import { validatePassword } from '../utils/passwordValidation';
import {
  authColors,
  authTitleSx,
  authSubtitleSx,
  authLabelSx,
  authTextFieldSx,
  authInputIconSx,
  authLinkAccentSx,
  authPrimaryButtonSx,
  authPanelFlexSx,
  authCloseButtonSx,
  authFieldErrorSx,
  authInfoAlertSx,
  authInfoAlertTextSx,
} from '../styles/authPanel.styles';

function FieldLabel({ children }) {
  return (
    <Typography component="label" sx={authLabelSx}>
      {children}
    </Typography>
  );
}

function PasswordToggleButton({ show, onToggle, disabled, labelShow, labelHide }) {
  return (
    <IconButton
      aria-label={show ? labelHide : labelShow}
      onClick={onToggle}
      onMouseDown={(event) => event.preventDefault()}
      edge="end"
      size="small"
      disabled={disabled}
      sx={{
        color: 'rgba(255,255,255,0.72)',
        '&:hover': { backgroundColor: 'transparent', color: authColors.textWhite },
      }}
    >
      {show ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
    </IconButton>
  );
}

const mapResetError = (message) => {
  const normalized = (message ?? '').toLowerCase();

  if (normalized.includes('expired')) {
    return 'El link expiró. Solicitá uno nuevo desde "Olvidé mi contraseña".';
  }
  if (normalized.includes('invalid') || normalized.includes('mismatch')) {
    return 'El link no es válido o ya fue usado. Solicitá uno nuevo.';
  }
  if (normalized.includes('limit exceeded')) {
    return 'Demasiados intentos. Esperá unos minutos e intentá de nuevo.';
  }

  return message || 'No pudimos restablecer tu contraseña. Intentá nuevamente.';
};

const resetTitleSx = {
  ...authTitleSx,
  mt: 0,
  mb: 0,
};

const resetSubtitleSx = {
  ...authSubtitleSx,
  mt: '12px',
  mb: 0,
  pr: 2,
};

const resetPanelSx = {
  ...authPanelFlexSx,
  height: '100%',
  minHeight: 0,
  overflowY: 'auto',
};

const resetContentSx = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  width: '100%',
  minHeight: '100%',
};

const resetHeaderSx = {
  flexShrink: 0,
  width: '100%',
  mb: { xs: 2, sm: 2.5 },
};

const resetBodySx = {
  flex: '1 0 auto',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  width: '100%',
  gap: 3,
};

const resetBackLinkSx = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '6px',
  width: '100%',
  flexShrink: 0,
};

const resetSuccessSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  width: '100%',
  py: { xs: 2, sm: 3 },
};

const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitStatus, setSubmitStatus] = useState('idle');
  const navigate = useNavigate();
  const location = useLocation();

  const resetParams = useMemo(
    () => getResetPasswordParams(
      new URLSearchParams(location.search),
      location.state,
      typeof window !== 'undefined' ? window.location.hash : '',
    ),
    [location.search, location.state]
  );

  const { email, code, isValid: hasResetToken } = resetParams;
  const isSubmitting = submitStatus === 'submitting';
  const isSuccess = submitStatus === 'success';

  const canSubmit =
    hasResetToken &&
    newPassword &&
    confirmPassword &&
    !isSubmitting &&
    !isSuccess;

  const handleResetPassword = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;

    setSubmitStatus('submitting');
    setError("");

    try {
      if (newPassword !== confirmPassword) {
        setError('Las contraseñas no coinciden');
        setSubmitStatus('idle');
        return;
      }

      const passwordError = validatePassword(newPassword);
      if (passwordError) {
        setError(passwordError);
        setSubmitStatus('idle');
        return;
      }

      const result = await confirmForgotPassword(email, code, newPassword);

      if (result.success) {
        setSubmitStatus('success');
        window.setTimeout(() => {
          navigate("/login", {
            state: { message: "Contraseña actualizada. Ya podés iniciar sesión." },
          });
        }, 2000);
        return;
      }

      setError(mapResetError(result.error));
      setSubmitStatus('idle');
    } catch (err) {
      console.error("Exception during confirm forgot password:", err);
      setError(mapResetError(err.message));
      setSubmitStatus('idle');
    }
  };

  return (
    <Paper elevation={0} sx={resetPanelSx}>
      <IconButton
        size="small"
        onClick={() => navigate("/app")}
        aria-label="Cerrar"
        sx={authCloseButtonSx}
      >
        <CloseIcon sx={{ fontSize: 22 }} />
      </IconButton>

      <Box sx={resetContentSx}>
        <Box sx={resetHeaderSx}>
          <Typography component="h1" sx={resetTitleSx}>
            Recuperar contraseña
          </Typography>
          <Typography component="p" sx={resetSubtitleSx}>
            {hasResetToken
              ? 'Ingresa tu nueva contraseña'
              : 'El link no es válido o expiró.'}
          </Typography>
        </Box>

        <Box sx={resetBodySx}>
          {!hasResetToken ? (
            <>
              <Box sx={{ ...authInfoAlertSx, mb: 0, width: '100%' }} role="alert">
                <Typography sx={{ ...authInfoAlertTextSx, minWidth: 0, wordBreak: 'break-word' }}>
                  Abrí el link que te enviamos por correo o solicitá uno nuevo.
                </Typography>
              </Box>

              <Box sx={{ width: '100%' }}>
                <Button
                  variant="contained"
                  fullWidth
                  disableElevation
                  onClick={() => navigate("/forgot-password")}
                  sx={authPrimaryButtonSx}
                >
                  Solicitar nuevo link
                </Button>

                <Box sx={{ ...resetBackLinkSx, mt: '24px' }}>
                  <ArrowBackIcon sx={{ fontSize: 18, color: authColors.linkAccent, flexShrink: 0 }} />
                  <Link
                    component="button"
                    type="button"
                    onClick={() => navigate("/login")}
                    sx={{
                      ...authLinkAccentSx,
                      fontSize: '16px',
                      lineHeight: '20px',
                    }}
                  >
                    Volver al login
                  </Link>
                </Box>
              </Box>
            </>
          ) : isSuccess ? (
            <Box role="status" aria-live="polite" sx={resetSuccessSx}>
              <CheckCircleOutlineIcon sx={{ fontSize: 52, color: colors.primaryLight }} />
              <Typography
                sx={{
                  fontFamily: typography.fontFamily.primary,
                  fontSize: '18px',
                  lineHeight: '22px',
                  fontWeight: typography.fontWeight.semibold,
                  color: authColors.textWhite,
                  textAlign: 'center',
                }}
              >
                ¡Contraseña actualizada!
              </Typography>
              <Typography
                sx={{
                  fontFamily: typography.fontFamily.primary,
                  fontSize: '16px',
                  lineHeight: '22px',
                  color: authColors.textSubtitle,
                  textAlign: 'center',
                }}
              >
                Te redirigimos al login...
              </Typography>
            </Box>
          ) : (
            <Box
              component="form"
              onSubmit={handleResetPassword}
              sx={{ width: '100%' }}
            >
              <Box sx={{ mb: '16px' }}>
                <FieldLabel>Contraseña</FieldLabel>
                <TextField
                  placeholder="Ingresá tu nueva contraseña"
                  variant="outlined"
                  fullWidth
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  disabled={isSubmitting}
                  autoComplete="new-password"
                  sx={authTextFieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ ml: '6px', mr: '4px' }}>
                        <LockOutlinedIcon sx={authInputIconSx} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end" sx={{ mr: '4px' }}>
                        <PasswordToggleButton
                          show={showNewPassword}
                          onToggle={() => setShowNewPassword((value) => !value)}
                          disabled={isSubmitting}
                          labelShow="Mostrar contraseña"
                          labelHide="Ocultar contraseña"
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box sx={{ mb: '8px' }}>
                <FieldLabel>Confirmar contraseña</FieldLabel>
                <TextField
                  placeholder="Confirmar nueva contraseña"
                  variant="outlined"
                  fullWidth
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  disabled={isSubmitting}
                  autoComplete="new-password"
                  sx={authTextFieldSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ ml: '6px', mr: '4px' }}>
                        <LockOutlinedIcon sx={authInputIconSx} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end" sx={{ mr: '4px' }}>
                        <PasswordToggleButton
                          show={showConfirmPassword}
                          onToggle={() => setShowConfirmPassword((value) => !value)}
                          disabled={isSubmitting}
                          labelShow="Mostrar confirmación de contraseña"
                          labelHide="Ocultar confirmación de contraseña"
                        />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {error && (
                <Typography sx={{ ...authFieldErrorSx, mb: '12px' }}>
                  {error}
                </Typography>
              )}

              <Box sx={{ mt: '24px', width: '100%' }}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disableElevation
                  disabled={!canSubmit}
                  sx={authPrimaryButtonSx}
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={18} thickness={5} sx={{ color: authColors.textWhite }} />
                    ) : null
                  }
                >
                  {isSubmitting ? 'Guardando...' : 'Aceptar'}
                </Button>
              </Box>
            </Box>
          )}
        </Box>

      </Box>
    </Paper>
  );
};

export default ResetPassword;
